import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'
import type { components } from '@/lib/api/generated'

export type VendorResponse = components['schemas']['VendorResponse']

// VendorUpsertRequest mirrors backend/schemas/vendor.py — same fields as
// VendorResponse but all optional. Define locally until gen-api regen.
export interface VendorUpsertRequest {
  vendor?: string | null
  supplier?: string | null
  contact_name?: string | null
  phone?: string | null
  email?: string | null
  payment_terms?: string | null
  lead_time?: number | null
  buffer_days?: number | null
  mov_dollars?: number | null
  free_shipping_dollars?: number | null
  duty_pct?: number | null
  freight_pct?: number | null
  country_of_origin?: string | null
  multiplier?: number | null
  notes?: string | null
  b2b_portal_url?: string | null
  b2b_username?: string | null
  b2b_password?: string | null
  duty_payer?: 'Dapper' | 'Vendor' | 'Split' | 'Duty-free' | null
}

export function usePatchVendor() {
  const qc = useQueryClient()
  // Use the query-param endpoint so vendor names containing "/" (e.g.
  // "5/A Baker") don't 404 behind nginx — path-encoding %2F was being
  // re-decoded by the proxy before FastAPI's {vendor:path} matcher saw it.
  return useMutation({
    mutationFn: ({ vendor, body }: { vendor: string; body: VendorUpsertRequest }) =>
      apiFetch<VendorResponse>('PATCH', '/api/vendors/_by-name', { query: { vendor }, body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

export function useCreateVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: VendorUpsertRequest) =>
      apiFetch<VendorResponse>('POST', '/api/vendors/', { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

export function useRestoreVendors() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ success: boolean; restored: number }>('POST', '/api/vendors/restore'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

export function useDeleteVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vendor: string) =>
      apiFetch<void>('DELETE', '/api/vendors/_by-name', { query: { vendor } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}
